import React, { useState, useEffect, useCallback, memo, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  Collapse,
  FormControlLabel,
  MenuItem,
  Autocomplete,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  InputAdornment,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearIcon from '@mui/icons-material/Clear';
import { supabase } from '../../supabaseClient';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Switch } from '@mui/material';
import { useDomainValues } from '../Lookup_components/document_domainvalueslookup';
import debounce from 'lodash/debounce';
import { v4 as uuidv4 } from 'uuid';

// Verplaats deze component naar buiten en gebruik memo
const AutocompleteField = memo(({ label, value, onChange, options, loading, keyPrefix, isReadOnly }) => {
  const [open, setOpen] = useState(false);
  const currentOption = useMemo(() => 
    options?.find(option => option.domain_value_id === value) || null,
    [options, value]
  );

  return (
    <Autocomplete
      fullWidth
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options || []}
      getOptionLabel={(option) => option.description || ''}
      value={currentOption}
      onChange={(event, newValue) => {
        onChange(newValue?.domain_value_id || null);
      }}
      disabled={isReadOnly}
      renderInput={(params) => (
        <TextField 
          {...params} 
          label={label} 
          fullWidth
          margin="normal" 
        />
      )}
      loading={loading}
      loadingText="Opties laden..."
      noOptionsText="Geen resultaten"
      renderOption={(props, option) => (
        <li {...props} key={`${keyPrefix}-${option.domain_value_id}-${option.description}`}>
          {option.description}
        </li>
      )}
      PopperProps={{ // Force dropdown to open downwards
        placement: 'bottom-start'
      }}
    />
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for memoization
  return (
    prevProps.value === nextProps.value &&
    prevProps.loading === nextProps.loading &&
    prevProps.keyPrefix === nextProps.keyPrefix &&
    // Simple comparison, might need deep comparison if options structure is complex
    prevProps.options === nextProps.options 
  );
});

// Separate component for Drillmaster to manage its own state for advanced Autocomplete features
const DrillmasterAutocompleteField = memo(({ point, persons, onSave, isReadOnly }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const currentPerson = useMemo(() => 
    persons.find(p => p.id === point.drillmaster) || null,
    [persons, point.drillmaster]
  );

  const filteredPersons = useMemo(() => {
    if (!inputValue) return persons;
    const lowerInputValue = inputValue.toLowerCase();
    return persons.filter(p => 
      (`${p.firstname || ''} ${p.lastname || ''}`.trim().toLowerCase().includes(lowerInputValue))
    );
  }, [persons, inputValue]);

  const handleInputChange = useCallback(debounce((event, newInputValue, reason) => {
    if (reason === 'input' || reason === 'clear') {
      setInputValue(newInputValue);
    }
  }, 50), []);

  const handleChange = (event, newValue) => {
    const updatedPoint = { ...point, drillmaster: newValue?.id || null };
    // We don't call setDrillPoints here, we call the onSave prop
    onSave(updatedPoint);
  };

  return (
    <Autocomplete
      fullWidth
      disableCloseOnSelect
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={filteredPersons}
      getOptionLabel={(option) => `${option.firstname || ''} ${option.lastname || ''}`.trim()}
      value={currentPerson}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      onChange={handleChange}
      onInputChange={handleInputChange}
      disabled={isReadOnly}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Drillmaster"
          fullWidth
          margin="normal"
          disabled={isReadOnly}
        />
      )}
      renderOption={(liProps, option) => {
        const { key, ...restLiProps } = liProps;
        const label = `${option.firstname || ''} ${option.lastname || ''}`.trim();
        return (
          <li 
            key={option.id}
            {...restLiProps} 
            style={{
              ...restLiProps.style,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block'
            }}
          >
            {label}
          </li>
        );
      }}
    />
  );
});

export default function DrillPointsTab({ document, isReadOnly }) {
  const [drillPoints, setDrillPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItem, setExpandedItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [persons, setPersons] = useState([]);
  // New state for create/delete functionality
  const [isCreating, setIsCreating] = useState(false);
  const [newDrillPoint, setNewDrillPoint] = useState({
    name: '',
    project_id: null,
    document_id: document.id
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [drillPointToDelete, setDrillPointToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const technicalNames = useMemo(() => [
    'MeetObjectSoort',
    'Eenheid',
    'MaaiveldType',
    'Situatiebeschrijving',
    'DrillMaster',
    'Hoedanigheid'
  ], []);
  const { domainValues, loading: domainValuesLoading, error: domainValuesError } = useDomainValues(technicalNames);

  useEffect(() => {
    fetchDrillPoints();
    fetchPersons();
  }, [document]);

  const fetchDrillPoints = async () => {
    try {
      const { data: projectData, error: projectError } = await supabase
        .from('document_project')
        .select('id')
        .eq('document_id', document.id)
        .single();

      if (projectError) throw projectError;

      setProjectId(projectData.id);

      const { data, error } = await supabase
        .from('document_borehole')
        .select('*')
        .eq('project_id', projectData.id)
        .order('created_at', { ascending: true }); // Keep consistent order

      if (error) throw error;
      setDrillPoints(data || []);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPersons = async () => {
    try {
      const { data, error } = await supabase
        .from('document_person')
        .select('id, firstname, lastname')
        .eq('document_id', document.id);
      
      if (error) throw error;
      setPersons(data || []);
    } catch (error) {
      console.error('Error fetching persons:', error);
    }
  };

  const validateDomainValue = (value, technicalName) => {
    if (!value) return true; // null/empty values are allowed
    const validValues = domainValues[technicalName] || [];
    return validValues.some(domain => domain.domain_value_id === value);
  };

  // Use a debounced save function similar to ProjectInfoTab
  const debouncedSave = useCallback(
    debounce(async (updatedPoint) => {
      if (isReadOnly) return;
      setSaving(true);
      try {
        console.log('Saving borehole data:', updatedPoint.id);
        
        const { error } = await supabase
          .from('document_borehole')
          .update(updatedPoint)
          .eq('id', updatedPoint.id);

        if (error) {
          console.error('Database error:', error);
          setError(`Fout bij opslaan: ${error.message}`);
        }
      } catch (err) {
        console.error('Save error:', err);
        setError(`Fout bij opslaan: ${err.message}`);
      } finally {
        setSaving(false);
      }
    }, 500),
    [isReadOnly]
  );

  const handleTextFieldChange = useCallback((point, fieldName, value) => {
    if (isReadOnly) return;
    
    // Handle numeric fields - convert empty strings to null
    let processedValue = value;
    if (['depth_value', 'groundLevel_value'].includes(fieldName)) {
      processedValue = value === '' ? null : (isNaN(parseFloat(value)) ? null : parseFloat(value));
    }
    
    // Create an updatedPoint object with the processed value
    const updatedPoint = { ...point, [fieldName]: processedValue };
    
    // Set local state for immediate UI feedback
    setDrillPoints(prev => prev.map(p => 
      p.id === point.id ? updatedPoint : p
    ));
    
    // Use debounced save for the database update
    debouncedSave(updatedPoint);
  }, [debouncedSave, isReadOnly]);

  const handleDateChange = useCallback((point, date) => {
    if (isReadOnly) return;
    const updatedPoint = { ...point, starttime: date?.toISOString() };
    setDrillPoints(prev => prev.map(p => 
      p.id === point.id ? updatedPoint : p
    ));
    debouncedSave(updatedPoint);
  }, [debouncedSave, isReadOnly]);

  const handleSwitchChange = useCallback((point, checked) => {
    if (isReadOnly) return;
    const updatedPoint = { ...point, isSuccessful: checked };
    setDrillPoints(prev => prev.map(p => 
      p.id === point.id ? updatedPoint : p
    ));
    debouncedSave(updatedPoint);
  }, [debouncedSave, isReadOnly]);

  const isValidDomainValue = (value, technicalName) => {
    if (!value) return true;
    if (!domainValues[technicalName]) return false;
    return domainValues[technicalName].some(
      option => option.domain_value_id === value
    );
  };

  const renderField = useCallback((point, fieldName, technicalName, label) => {
    // Special case for drillmaster field
    if (fieldName === 'drillmaster') {
      return (
        <Autocomplete
          key={`${point.id}-${fieldName}`}
          fullWidth
          options={persons}
          getOptionLabel={(option) => `${option.firstname || ''} ${option.lastname || ''}`.trim()}
          value={persons.find(p => p.id === point[fieldName]) || null}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          onChange={(event, newValue) => {
            const updatedPoint = { ...point, [fieldName]: newValue?.id || null };
            setDrillPoints(prev => prev.map(p => 
              p.id === point.id ? updatedPoint : p
            ));
            debouncedSave(updatedPoint);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              fullWidth
              margin="normal"
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {`${option.firstname || ''} ${option.lastname || ''}`.trim()}
            </li>
          )}
          isReadOnly={isReadOnly}
        />
      );
    }

    // Original renderField logic for other fields
    const options = domainValues[technicalName] || [];
    const handleFieldChange = (newValue) => {
      const updatedPoint = { ...point, [fieldName]: newValue };
      setDrillPoints(prev => prev.map(p => 
        p.id === point.id ? updatedPoint : p
      ));
      debouncedSave(updatedPoint);
    };

    return (
      <AutocompleteField
        key={`${point.id}-${fieldName}`}
        label={label}
        value={point[fieldName]}
        onChange={handleFieldChange}
        options={options}
        loading={loading || saving}
        keyPrefix={`${fieldName}-${point.id}`}
        isReadOnly={isReadOnly}
      />
    );
  }, [domainValues, loading, saving, persons, debouncedSave]);

  // Handle creating a new drill point
  const handleOpenCreateDialog = () => {
    if (isReadOnly) return;
    setNewDrillPoint({
      name: '',
      project_id: projectId,
      document_id: document.id
    });
    setIsCreating(true);
  };

  const handleCloseCreateDialog = () => {
    setIsCreating(false);
  };

  const handleCreateDrillPoint = async () => {
    if (isReadOnly) return;
    try {
      setIsSubmitting(true);
      
      // Normalize unit fields to extract only domain_value_id
      const normalizeUnitField = (unitField) => {
        if (!unitField) return null;
        if (typeof unitField === 'string') return unitField; // Already a UUID
        if (typeof unitField === 'object' && unitField.domain_value_id) {
          return unitField.domain_value_id; // Extract UUID from object
        }
        return null;
      };
      
      const createData = {
        id: uuidv4(),
        ...newDrillPoint,
        // Normalize unit fields if they exist
        depth_uom: normalizeUnitField(newDrillPoint.depth_uom),
        groundLevel_uom: normalizeUnitField(newDrillPoint.groundLevel_uom),
        depth_condition: normalizeUnitField(newDrillPoint.depth_condition),
        groundleveltype: normalizeUnitField(newDrillPoint.groundleveltype),
        measurementObjectType: normalizeUnitField(newDrillPoint.measurementObjectType),
      };
      
      const { data, error } = await supabase
        .from('document_borehole')
        .insert([createData])
        .select();
      
      if (error) throw error;
      
      setIsCreating(false);
      
      // Refresh drill points
      await fetchDrillPoints();
      
      // Expand the newly created item
      if (data && data.length > 0) {
        setExpandedItem(data[0].id);
      }
      
    } catch (error) {
      console.error('Error creating drill point:', error);
      setError(`Fout bij aanmaken boring: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle deleting a drill point
  const handleOpenDeleteDialog = (point) => {
    if (isReadOnly) return;
    setDrillPointToDelete(point);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteDrillPoint = async () => {
    if (isReadOnly || !drillPointToDelete) return;
    
    try {
      setIsDeleting(true);
      
      const { error } = await supabase
        .from('document_borehole')
        .delete()
        .eq('id', drillPointToDelete.id);
      
      if (error) throw error;
      
      // Close dialog and reset state
      setIsDeleteDialogOpen(false);
      setDrillPointToDelete(null);
      
      // Refresh drill points
      await fetchDrillPoints();
      
      // Clear expanded item if it was deleted
      if (expandedItem === drillPointToDelete.id) {
        setExpandedItem(null);
      }
      
    } catch (error) {
      console.error('Error deleting drill point:', error);
      setError(`Fout bij verwijderen boring: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading || domainValuesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Empty state with create button
  if (drillPoints.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Boringen</Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography sx={{ mb: 2 }}>Geen boringen gevonden</Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
            disabled={isReadOnly}
          >
            Nieuwe Boring
          </Button>
          
          {/* Create Dialog */}
          <Dialog open={isCreating} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
            <DialogTitle>Nieuwe Boring</DialogTitle>
            <DialogContent>
              <TextField
                label="Naam"
                value={newDrillPoint.name}
                onChange={(e) => setNewDrillPoint(prev => ({ ...prev, name: e.target.value }))}
                fullWidth
                margin="normal"
                autoFocus
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseCreateDialog}>Annuleren</Button>
              <Button 
                onClick={handleCreateDrillPoint} 
                variant="contained" 
                disabled={isSubmitting || isReadOnly}
                startIcon={isSubmitting ? <CircularProgress size={24} /> : null}
              >
                {isSubmitting ? 'Bezig met aanmaken...' : 'Aanmaken'}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Boringen</Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {domainValuesError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {domainValuesError}
        </Alert>
      )}

      {/* Add new boring button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          disabled={isReadOnly}
        >
          Nieuwe Boring
        </Button>
      </Box>

      {/* Create Dialog */}
      <Dialog open={isCreating} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Nieuwe Boring</DialogTitle>
        <DialogContent>
          <TextField
            label="Naam"
            value={newDrillPoint.name}
            onChange={(e) => setNewDrillPoint(prev => ({ ...prev, name: e.target.value }))}
            fullWidth
            margin="normal"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Annuleren</Button>
          <Button 
            onClick={handleCreateDrillPoint} 
            variant="contained" 
            disabled={isSubmitting || isReadOnly}
            startIcon={isSubmitting ? <CircularProgress size={24} /> : null}
          >
            {isSubmitting ? 'Bezig met aanmaken...' : 'Aanmaken'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
        <DialogTitle>Boring verwijderen</DialogTitle>
        <DialogContent>
          <Typography>
            Weet u zeker dat u deze boring wilt verwijderen? 
            Dit kan niet ongedaan worden gemaakt.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>
            Annuleren
          </Button>
          <Button 
            onClick={handleDeleteDrillPoint}
            color="error"
            disabled={isDeleting || isReadOnly}
            startIcon={isDeleting ? <CircularProgress size={24} /> : null}
          >
            {isDeleting ? 'Bezig met verwijderen...' : 'Verwijderen'}
          </Button>
        </DialogActions>
      </Dialog>

      <List>
        {drillPoints.map((point) => (
          <React.Fragment key={point.id}>
            <ListItem
              divider
              onClick={() => setExpandedItem(expandedItem === point.id ? null : point.id)}
              sx={{ cursor: 'pointer' }}
            >
              <ListItemText
                primary={point.name || 'Naamloos'}
              />
              <IconButton 
                edge="end" 
                color="error" 
                onClick={(e) => {
                  e.stopPropagation(); // Prevent expanding when clicking delete
                  handleOpenDeleteDialog(point);
                }}
                sx={{ mr: 1 }}
                disabled={isReadOnly}
              >
                <DeleteIcon />
              </IconButton>
              <IconButton edge="end">
                {expandedItem === point.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </ListItem>
            <Collapse in={expandedItem === point.id} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                {/* ...existing form fields... */}
                <TextField
                  fullWidth
                  label="Naam"
                  margin="normal"
                  value={point.name || ''}
                  onChange={(e) => handleTextFieldChange(point, 'name', e.target.value)}
                  inputProps={{ spellCheck: 'false' }}
                  disabled={isReadOnly}
                />
                <TextField
                  fullWidth
                  label="Opmerkingen"
                  margin="normal"
                  multiline
                  rows={4}
                  value={point.remarks || ''}
                  onChange={(e) => handleTextFieldChange(point, 'remarks', e.target.value)}
                  inputProps={{ spellCheck: 'false' }}
                  disabled={isReadOnly}
                />
                <DatePicker
                  label="Startdatum"
                  value={point.starttime ? new Date(point.starttime) : null}
                  onChange={(date) => handleDateChange(point, date)}
                  format="dd-MM-yyyy"
                  disabled={isReadOnly}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: "normal",
                      InputProps: {
                        endAdornment: (
                          <>
                            {point.starttime && (
                              <InputAdornment position="end" sx={{ marginRight: -1.5 }}>
                                <IconButton
                                  aria-label="clear date"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDateChange(point, null);
                                  }}
                                  edge="end"
                                  size="small"
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            )}
                          </>
                        ),
                      }
                    }
                  }}
                />
                {/* Wrap Depth value and unit in a flex Box */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}> 
                  <TextField
                    sx={{ width: '50%' }} // Adjust width as needed
                    label="Diepte"
                    margin="normal"
                    type="number"
                    value={point.depth_value || ''}
                    onChange={(e) => handleTextFieldChange(point, 'depth_value', e.target.value)}
                    inputProps={{ 
                      step: "0.01",
                      spellCheck: 'false'
                    }}
                    disabled={isReadOnly}
                  />
                  {/* Render depth unit field within the Box */}
                  <Box sx={{ width: '50%' }}> 
                    {renderField(point, 'depth_uom', 'Eenheid', 'Diepte eenheid')}
                  </Box>
                </Box>
                {/* Use filtered data for depth_condition */}
                {renderField(point, 'depth_condition', 'Hoedanigheid', 'Diepte conditie')}
                {renderField(point, 'groundleveltype', 'MaaiveldType', 'Maaiveld type')}
               
                <FormControlLabel
                  control={
                    <Switch
                      checked={point.isSuccessful || false}
                      onChange={(e) => handleSwitchChange(point, e.target.checked)}
                      disabled={isReadOnly}
                    />
                  }
                  label="Succesvol"
                />
                <TextField
                  fullWidth
                  label="Maaiveld waarde"
                  margin="normal"
                  type="number"
                  value={point.groundLevel_value || ''}
                  onChange={(e) => handleTextFieldChange(point, 'groundLevel_value', e.target.value)}
                  inputProps={{ 
                    step: "0.01",
                    spellCheck: 'false'
                  }}
                    disabled={isReadOnly}
                />
                {renderField(point, 'groundLevel_uom', 'Eenheid', 'Maaiveld eenheid')}
                {renderField(point, 'measurementObjectType', 'MeetObjectSoort', 'Meetobject type')}
              </Box>
            </Collapse>
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
}
